import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

// Base / Form Components
import { Button } from "@/components/v6/base/button";

// Related Components

const FLASH_EVENT_NAME = "oomple:flash";
const DEFAULT_TIMEOUT = 5000;

const createToast = ({ type, message }) => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  type: type || "notice",
  message: message || "Success!",
});

const stylesFor = (type) => {
  if (type === "alert" || type === "error") {
    return {
      wrapper: "border-red-200 bg-red-50 text-red-900",
      title: "text-red-950",
      button: "text-red-700 hover:text-red-950",
    };
  }

  return {
    wrapper: "border-emerald-200 bg-emerald-50 text-emerald-900",
    title: "text-emerald-950",
    button: "text-emerald-700 hover:text-emerald-950",
  };
};

const titleFor = (type) => {
  if (type === "alert" || type === "error") return "Something went wrong";
  return "Success";
};

const FlashToasterDefaultProps = {
  initialToasts: [],
  timeout: DEFAULT_TIMEOUT,
};

export default function FlashToaster({ initialToasts = FlashToasterDefaultProps.initialToasts, timeout = FlashToasterDefaultProps.timeout }) {
  const [toasts, setToasts] = useState(
    () => initialToasts.map(createToast)
  );

  useEffect(() => {
    const initialTimeouts = toasts.map((toast) => (
      window.setTimeout(() => {
        setToasts((currentToasts) => (
          currentToasts.filter((currentToast) => currentToast.id !== toast.id)
        ));
      }, timeout)
    ));

    const handleFlash = (event) => {
      const toast = createToast(event.detail || {});

      setToasts((currentToasts) => [
        ...currentToasts,
        toast,
      ]);

      window.setTimeout(() => {
        setToasts((currentToasts) => (
          currentToasts.filter((currentToast) => currentToast.id !== toast.id)
        ));
      }, timeout);
    };

    window.addEventListener(FLASH_EVENT_NAME, handleFlash);

    return () => {
      initialTimeouts.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });

      window.removeEventListener(FLASH_EVENT_NAME, handleFlash);
    };
  }, []);

  const removeToast = (id) => {
    setToasts((currentToasts) => (
      currentToasts.filter((toast) => toast.id !== id)
    ));
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed left-1/2 top-5 z-[9999] flex w-[min(420px,calc(100vw-40px))] -translate-x-1/2 flex-col gap-3">
      {toasts.map((toast) => {
        const styles = stylesFor(toast.type);

        return (
          <div
            key={toast.id}
            className={`rounded-xl border px-4 py-3 shadow-lg ${styles.wrapper}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className={`text-sm font-bold ${styles.title}`}>
                  {titleFor(toast.type)}
                </div>

                <div className="mt-1 text-sm leading-5">
                  {toast.message}
                </div>
              </div>

              <Button
                type="button"
                variant="ghost"
                className={`h-auto p-0 text-lg leading-none hover:bg-transparent ${styles.button}`}
                onClick={() => removeToast(toast.id)}
              >
                &times;
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

FlashToaster.propTypes = {
  initialToasts: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      message: PropTypes.string,
    })
  ),
  timeout: PropTypes.number,
};



export {
  FLASH_EVENT_NAME,
};
